import React from 'react';
import { Navigate, Link } from 'react-router-dom';

/**
 * Route guard that checks authentication and optionally enforces page-level permissions.
 * 
 * Usage:
 *   <ProtectedRoute user={user} loading={loading}>  — auth only
 *   <ProtectedRoute user={user} loading={loading} can={can} requiredPermission={PAGE_PERMISSIONS.DASHBOARD}>  — auth + single permission
 *   <ProtectedRoute user={user} loading={loading} can={can} requiredPermissions={[P1, P2]}>  — auth + any of multiple permissions
 */
const ProtectedRoute = ({ user, loading, can, requiredPermission, requiredPermissions, children }) => {
    if (loading) {
        return <div>Loading...</div>;
    }
    if (!user) {
        return <Navigate to="/login" replace />;
    }

    // Permission enforcement (when can + permission props are provided)
    if (can && (requiredPermission || requiredPermissions)) {
        const hasAccess = requiredPermissions
            ? requiredPermissions.some(p => can(p))
            : can(requiredPermission);

        if (!hasAccess) {
            return (
                <div className="flex items-center justify-center min-h-[60vh]">
                    <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-lg">
                        <div className="text-6xl mb-4">🔒</div>
                        <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
                        <p className="text-gray-600 mb-6">
                            You don't have permission to access this page. Contact your administrator if you believe this is an error.
                        </p>
                        <Link
                            to="/profile"
                            className="inline-block px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                        >
                            Go to Profile
                        </Link>
                    </div>
                </div>
            );
        }
    }

    return children;
};

export default ProtectedRoute;